import { useState, useCallback } from 'react';
import { config } from '../utils/config';
import { REQUEST_TIMEOUT } from '../constants';
import { 
  isStandardApiResponse, 
  ApiVersion,
  ApiRequestConfig,
  ValidationErrors,
  ApiMeta
} from '../types/api';

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  validationErrors: ValidationErrors | null;
  meta: ApiMeta | null;
  message: string | null;
}

interface UseApiOptions extends ApiRequestConfig {
  timeout?: number;
  baseURL?: string;
  /** デフォルトはv2（統一形式） */
  version?: ApiVersion;
  /** Cookie認証を有効にするかどうか */
  credentials?: RequestCredentials;
}

interface UseApiReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** 統一形式のバリデーションエラー */
  validationErrors: ValidationErrors | null;
  /** APIメタデータ */
  meta: ApiMeta | null;
  execute: (url: string, options?: RequestInit) => Promise<T | null>;
  reset: () => void;
  /** 統一形式の場合のメッセージ */
  message: string | null;
}

/**
 * API呼び出し用カスタムフック
 * ローディング状態、エラーハンドリング、タイムアウト機能を提供
 */
export const useApi = <T = unknown>(options: UseApiOptions = {}): UseApiReturn<T> => {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
    validationErrors: null,
    meta: null,
    message: null,
  });

  // リクエスト制限用（同時リクエスト数制御）
  const [, setPendingRequests] = useState<Set<string>>(new Set());

  const execute = useCallback(async (url: string, requestOptions: RequestInit = {}): Promise<T | null> => {
    // 重複リクエストチェック
    const requestKey = `${url}_${JSON.stringify(requestOptions)}`;
    
    // 現在のpendingRequestsを確認（setState内でアクセス）
    let shouldProceed = true;
    setPendingRequests(prev => {
      if (prev.has(requestKey)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('重複リクエストをブロックしました:', url);
        }
        shouldProceed = false;
        return prev; // 変更せずに返す
      }
      return new Set(prev).add(requestKey); // 新しいリクエストを追加
    });

    if (!shouldProceed) {
      return null;
    }
    
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null, 
      validationErrors: null,
      message: null 
    }));

    try {
      const baseURL = options.baseURL || config.apiUrl;
      const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || REQUEST_TIMEOUT.DEFAULT);

      const finalHeaders = {
        ...requestOptions.headers,
        'Content-Type': 'application/json',
        // 統一形式をリクエスト（デフォルト）
        'X-API-Version': options.version || 'v2',
      };
      
      // 開発環境でのみデバッグログを表示（セキュリティ強化）
      if (process.env.NODE_ENV === 'development') {
        const logSafeOptions = { 
          ...requestOptions, 
          body: requestOptions.body ? '[BODY_CONTENT_HIDDEN]' : undefined 
        };
        const safeHeaders = {
          ...finalHeaders,
          Authorization: (finalHeaders as any).Authorization ? 'Bearer [TOKEN_HIDDEN]' : undefined
        };
        console.log('API request:', { fullUrl, options: logSafeOptions });
        console.log('🔍 最終ヘッダー:', safeHeaders);
      }
      
      const response = await fetch(fullUrl, {
        ...requestOptions,
        signal: controller.signal,
        headers: finalHeaders,
        credentials: options.credentials || 'same-origin', // Cookie認証を有効化
      });

      clearTimeout(timeoutId);
      if (process.env.NODE_ENV === 'development') {
        console.log('API response:', { status: response.status, ok: response.ok });
      }

      // JSON以外（例えば空ボディやHTML）に耐性を持たせる
      let result: any = null;
      const ct = response.headers.get('Content-Type') || '';
      if (ct.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        try { result = JSON.parse(text); } catch { result = { status: response.ok ? 'success' : 'error', message: text || null }; }
      }
      if (process.env.NODE_ENV === 'development') {
        // セキュリティ: ユーザー情報を含む可能性があるレスポンスを安全に表示
        const safeResult = {
          status: result.status,
          message: result.message,
          meta: result.meta,
          // dataは機密情報を含む可能性があるため、存在確認のみ表示
          hasData: !!result.data,
          dataType: result.data ? typeof result.data : 'undefined'
        };
        console.log('API result (safe):', safeResult);
      }
      
      // 統一形式のレスポンス処理
      if (isStandardApiResponse(result)) {
        if (result.status === 'success') {
          setState({ 
            data: result.data as T, 
            loading: false, 
            error: null,
            validationErrors: null,
            meta: result.meta,
            message: result.message || null
          });
          return result.data as T;
        } else {
          // エラーレスポンス
          const errorMessage = result.message || 'APIエラーが発生しました';
          const validationErrors = typeof result.errors === 'object' && result.errors !== null 
            ? result.errors as ValidationErrors 
            : null;
          
          setState({ 
            data: null, 
            loading: false, 
            error: errorMessage,
            validationErrors,
            meta: result.meta,
            message: result.message || null
          });
          return null;
        }
      }
      // 不明な形式の場合
      else {
        if (!response.ok) {
          if (process.env.NODE_ENV === 'development') {
            console.error('API error response:', result);
          }
          throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
        }
        setState({ 
          data: result as T, 
          loading: false, 
          error: null,
          validationErrors: null,
          meta: null,
          message: null
        });
        return result as T;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMessage,
        validationErrors: null,
        meta: null,
        message: null
      }));
      return null;
    } finally {
      // リクエスト完了後にpendingRequestsから削除
      setPendingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestKey);
        return newSet;
      });
    }
  }, [options.baseURL, options.timeout, options.version]); // pendingRequestsを依存関係から除外

  const reset = useCallback(() => {
    setState({ 
      data: null, 
      loading: false, 
      error: null,
      validationErrors: null,
      meta: null,
      message: null
    });
  }, []);

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    validationErrors: state.validationErrors,
    meta: state.meta,
    message: state.message,
    execute,
    reset,
  };
}; 

// Analytics API関連
export const useUserStatsApi = () => {
  const { execute } = useApi();

  const getAllUserStats = async () => {
    const token = localStorage.getItem('authToken');
    const cleanToken = token ? token.replace(/"/g, '') : '';
    
    return await execute('/analytics/users/stats/', {
      method: 'GET',
      headers: cleanToken ? { 'Authorization': `Bearer ${cleanToken}` } : undefined,
    });
  };

  const getUserStatsDetail = async (userId: number) => {
    const token = localStorage.getItem('authToken');
    const cleanToken = token ? token.replace(/"/g, '') : '';
    
    return await execute(`/api/analytics/users/${userId}/stats/`, {
      method: 'GET',
      headers: cleanToken ? { 'Authorization': `Bearer ${cleanToken}` } : undefined,
    });
  };

  const getUserProjects = async (userId?: number) => {
    const endpoint = userId 
      ? `/analytics/users/${userId}/projects/`
      : '/analytics/projects/';
    
    const token = localStorage.getItem('authToken');
    const cleanToken = token ? token.replace(/"/g, '') : '';
    
    return await execute(endpoint, {
      method: 'GET',
      headers: cleanToken ? { 'Authorization': `Bearer ${cleanToken}` } : undefined,
    });
  };

  return {
    getAllUserStats,
    getUserStatsDetail,
    getUserProjects,
  };
}; 